import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  IconButton,
  TextField,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, addDoc, updateDoc, query, orderBy } from 'firebase/firestore';

const AdminComites = () => {
  const [comites, setComites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [comiteToDelete, setComiteToDelete] = useState(null);
  const [actionStatus, setActionStatus] = useState({ success: false, error: null, message: '' });
  
  // Estado para el formulario de agregar/editar comité
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingComite, setEditingComite] = useState(null);
  const [comiteForm, setComiteForm] = useState({
    nombre: '',
    descripcion: ''
  });

  // Cargar todos los comités al iniciar el componente
  useEffect(() => {
    fetchComites();
  }, []);

  const fetchComites = async () => {
    try {
      setLoading(true);
      const comitesRef = collection(db, 'comites');
      const q = query(comitesRef, orderBy('nombre'));
      const querySnapshot = await getDocs(q);
      
      const comitesData = [];
      querySnapshot.forEach((doc) => {
        comitesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setComites(comitesData);
      setError(null);
    } catch (error) {
      console.error('Error al cargar comités:', error);
      setError('Error al cargar los comités. Por favor intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Abrir diálogo de confirmación para eliminar comité
  const handleDeleteClick = (comite) => {
    setComiteToDelete(comite);
    setDeleteDialogOpen(true);
  };

  // Cerrar diálogo de confirmación
  const handleCloseDialog = () => {
    setDeleteDialogOpen(false);
    setComiteToDelete(null);
  };

  // Eliminar comité
  const handleDeleteConfirm = async () => {
    if (!comiteToDelete) return;
    
    try {
      setLoading(true);
      // Eliminar el comité de Firestore
      await deleteDoc(doc(db, 'comites', comiteToDelete.id));
      
      // Actualizar la lista de comités local
      setComites(prevComites => prevComites.filter(comite => comite.id !== comiteToDelete.id));
      
      // Mostrar mensaje de éxito
      setActionStatus({ 
        success: true, 
        error: null, 
        message: 'Comité eliminado correctamente.' 
      });
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
      
      // Cerrar el diálogo
      handleCloseDialog();
    } catch (error) {
      console.error('Error al eliminar comité:', error);
      setActionStatus({ 
        success: false, 
        error: true, 
        message: 'Error al eliminar el comité. Por favor intente nuevamente.' 
      });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Abrir formulario para agregar nuevo comité
  const handleAddClick = () => {
    setEditingComite(null);
    setComiteForm({
      nombre: '',
      descripcion: ''
    });
    setFormDialogOpen(true);
  };

  // Abrir formulario para editar comité existente
  const handleEditClick = (comite) => {
    setEditingComite(comite);
    setComiteForm({
      nombre: comite.nombre,
      descripcion: comite.descripcion || ''
    });
    setFormDialogOpen(true);
  };

  // Cerrar formulario
  const handleCloseForm = () => {
    setFormDialogOpen(false);
    setEditingComite(null);
  };

  // Manejar cambios en el formulario
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setComiteForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Guardar comité (nuevo o editado)
  const handleSaveComite = async () => {
    // Validar que el nombre no esté vacío
    if (!comiteForm.nombre.trim()) {
      setActionStatus({
        success: false,
        error: true,
        message: 'El nombre del comité es obligatorio.'
      });
      return;
    }

    try {
      setLoading(true);
      
      if (editingComite) {
        // Actualizar comité existente
        const comiteRef = doc(db, 'comites', editingComite.id);
        await updateDoc(comiteRef, {
          nombre: comiteForm.nombre.trim(),
          descripcion: comiteForm.descripcion.trim(),
          updatedAt: new Date()
        });
        
        // Actualizar la lista local
        setComites(prevComites => prevComites.map(comite => 
          comite.id === editingComite.id ? 
          { 
            ...comite, 
            nombre: comiteForm.nombre.trim(), 
            descripcion: comiteForm.descripcion.trim() 
          } : comite
        ));
        
        setActionStatus({
          success: true,
          error: null,
          message: 'Comité actualizado correctamente.'
        });
      } else {
        // Crear nuevo comité
        const newComite = {
          nombre: comiteForm.nombre.trim(),
          descripcion: comiteForm.descripcion.trim(),
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        const docRef = await addDoc(collection(db, 'comites'), newComite);
        
        // Añadir a la lista local
        setComites(prevComites => [
          ...prevComites, 
          { id: docRef.id, ...newComite }
        ]);
        
        setActionStatus({
          success: true,
          error: null,
          message: 'Comité agregado correctamente.'
        });
      }
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
      
      // Cerrar el formulario
      handleCloseForm();
    } catch (error) {
      console.error('Error al guardar comité:', error);
      setActionStatus({
        success: false,
        error: true,
        message: `Error al ${editingComite ? 'actualizar' : 'crear'} el comité. Por favor intente nuevamente.`
      });
      
      // Limpiar mensaje de error después de 3 segundos
      setTimeout(() => {
        setActionStatus({ success: false, error: null, message: '' });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={500} color="primary">
          Administración de Comités
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
        >
          Nuevo Comité
        </Button>
      </Box>
      
      {actionStatus.message && (
        <Alert severity={actionStatus.error ? "error" : "success"} sx={{ mb: 2 }}>
          {actionStatus.message}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading && comites.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No hay comités registrados
                  </TableCell>
                </TableRow>
              ) : (
                comites.map((comite) => (
                  <TableRow key={comite.id}>
                    <TableCell>{comite.nombre}</TableCell>
                    <TableCell>{comite.descripcion || '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton onClick={() => handleEditClick(comite)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleDeleteClick(comite)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el comité "{comiteToDelete?.nombre}"? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Formulario para agregar/editar comité */}
      <Dialog
        open={formDialogOpen}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingComite ? 'Editar Comité' : 'Nuevo Comité'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="nombre"
                label="Nombre del Comité"
                value={comiteForm.nombre}
                onChange={handleFormChange}
                fullWidth
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={comiteForm.descripcion}
                onChange={handleFormChange}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleSaveComite} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminComites;